import { useState } from "react"
import { Sidebar } from "src/components/Sidebar/Sidebar"
import { ProjectImage } from "src/components/ProjectImage/ProjectImage"
import YouTubeSummarizer from "src/components/youtubeSummarizer/YouTubeSummarizer"
import { Modal } from "antd"
import "./styles.scss"

const MainContent = () => {
  return (
    <div className="main-content p-4">
      <div className="header mb-4">
        <h1 className="title">YouTube Summarizer</h1>
        <h2 className="subtitle">AI-Powered YouTube Video Summarization Tool</h2>
      </div>

      <div className="description mb-5">
        <p>
        This is a web app I created to deliver scheduled summaries of YouTube videos via email. As an avid YouTube user, I consume much of my news through the platform. I designed this tool to enhance content consumption efficiency and deliver information in a more fact-focused format.
        </p>
        <p>
        Feel free to explore the tool yourself by clicking the view tool button to the left. The tool is completely free to use, and I'm excited for you to try it out!
        </p>
      </div>

      <div className="description mb-5">
        <h4>Things I Worked On</h4>
        <p>While building this YouTube Summarizer, I worked on:</p>
        <ul>
          <li><a href="#frontend-development" className="work-link">Frontend Development</a></li>
          <li><a href="#backend-api" className="work-link">Backend API</a></li>
          <li><a href="#api-integration" className="work-link">API Integration</a></li>
          <li><a href="#email-delivery" className="work-link">Email Delivery System</a></li>
        </ul>
      </div>

      <div id="frontend-development" className="description mb-5">
        <h4>Frontend Development</h4>
        <p>
          I built the frontend using React with TypeScript. The interface allows users to input a YouTube URL and their email address to receive a summarized transcript.
        </p>
      </div>
      <ProjectImage 
        src="/assets/Screenshots/summarizerMain.png" 
        alt="summarizer tool"
        description="Clean and intuitive UI of the tool"
        width="60%"
      />
      <div id="backend-api" className="description mb-5">
        <h4>Backend</h4>
        <p>
            In my planning phase, I wanted the tool to work via several endpoints, 
            which would interact with free APIs to fetch the content, generate summaries, and send emails. 
            I initially was excited to integrate MCP technology into the project, but I was unable to get it working. 
            If I were to do this again, I would use a different language (Node.JS), where the support is stronger for MCP.
        </p>
        <h5>C#, .NET</h5>        
        <p>
          The backend was developed using C# and .NET, which was a new technology stack for me. 
          I took this opportunity to gain some basic familiarity with the stack, 
          and learned how it differs from the Node.js backends I've built in the past. 
          C# was difficult to understand at first, but I feel as if now I have basic familiarity.
        </p>
        <p> 
          It became clear that C# was not the best suited for the task, especially given my reliance on AI APIs and SDKs, 
          as I found that C# is further behind in terms of support for these technologies.
          That said, I was able to complete the project with C#, and I hope to do another project in the future that's more suited toward the stack.
        </p>
        <h5>Google Cloud</h5>
        <p>
        The backend is hosted in Google Cloud Run, and I am using Cloud Scheduler to kick off the process every morning to check for new videos from particular channels.
        </p>
      </div>

      <div id="api-integration" className="description mb-5">
        <h4>API Integration</h4>
        <p>
          I integrated several external APIs during this process. This became the most difficult part of the project, as they had certain limitations that I initially did not account for.
        </p>
        <h5>YouTube API</h5>
        <p>
            To get YouTube transcripts, I initially integrated the YouTube Explode package into my instance. 
            This worked well locally, but when I went to deploy it broke, as I found out that YouTube blocks direct bot access from Cloud Servers.
            I ended up using a new API called <a href="https://supadata.ai/" target="_blank" rel="noopener noreferrer">SupaData</a>, which works great, and also uses MCP. Unfortunately, their free API is limited to 100 requests per month.
        </p>
        <h5>Claude API</h5>
        <p>
            For AI generation, I used the Claude API. I used a basic multi-step RAG process to generate the summaries. First, I would generate a summary based on the transcript. 
            Next I asked Claude to generate search phrases to further research and fact check the topic. After retrieving links from the Brave API, I used Claude again to structure the result in consistent HTML.    
        </p>
        <h5>Brave API</h5>
        <p>
           My idea of how I would use MCP was to allow Claude access to the Brave API to search the web for related content, and do independent fact checking. While I believe this would work on Claude desktop, I found the MCP support within C# to be lacking, so I ended up implementing the more structured approach above.
           This worked well, but I believe if I could have used MCP, it would have saved per query costs and time. Also, about a week after I finished the project, Claude came out with built-in search in their APIs, which is another option to get this functionality.
        </p>
        <ProjectImage 
        src="/assets/Screenshots/summarizerRelatedLinks.png" 
        alt="summarizer tool"
        description="Related links from the Brave API"
        width="80%"
      />
      </div>

      <div id="email-delivery" className="description mb-5">
        <h4>Email Delivery System</h4>
        <p>
         I implemented SendGrid to deliver the emails. SendGrid is certainly finicky, but this project helped me understand what it takes to get their free version working well. Here are some output examples:
        </p>
        <ProjectImage 
        src="/assets/Screenshots/summarizerTop.png" 
        alt="summarizer tool"
        description="Links to the original video"
        width="80%"
      />
      <ProjectImage 
        src="/assets/Screenshots/summarizerSummary.png" 
        alt="summarizer tool"
        description="Summary of the video"
        width="80%"
      />
      </div>

      <div id="conclusion" className="description mb-5">
        <h4>Conclusion</h4>
        <p>
          This project was a valuable learning experience that allowed me to explore new technologies and AI capabilities. While C# presented some challenges for AI integration, I was able to overcome them by implementing a structured approach to the summarization process.
        </p>
        <p>
          The combination of YouTube transcript extraction, Claude's AI summarization, and Brave's search API created a powerful tool that delivers concise, fact-checked summaries of video content. The email delivery system ensures users can consume this information conveniently.
        </p>
        <p>
          This was a fun, and informative project to work on, and I ended up building a tool that I use regularly.
        </p>
      </div>
    </div>
  )
}

interface ProjectLayoutProps {
  sidebar: React.ReactNode;
  children: React.ReactNode;
}

const ProjectLayout: React.FC<ProjectLayoutProps> = ({ sidebar, children }) => (
  <div className="container-fluid p-0">
    <div className="row g-0">
      <div className="col-md-3">{sidebar}</div>
      <div className="col-md-9">{children}</div>
    </div>
  </div>
)

export default function YoutubeSummarizerProject() {
    const [isModalVisible, setIsModalVisible] = useState(false);
  
    const showModal = () => {
      setIsModalVisible(true);
    };
  
    const handleCancel = () => {
      setIsModalVisible(false);
    };
  
    const sidebarProps = {
      toolUrl: "#",
      onToolClick: showModal,  // Custom prop to handle the click
      responsibilities: [
        "React",
        "TypeScript",
        "Ant Design",
        "C# / .NET",
        "SupaData API",
        "Model Context Protocol (MCP)",
        "Brave API",
        "SendGrid",
        "Claude Sonnet 3.5 API"
      ]
    }
    
    return (
      <>
        <ProjectLayout sidebar={<Sidebar {...sidebarProps} />}>
          <MainContent />
        </ProjectLayout>
        
        <Modal
          title="YouTube Summarizer Tool"
          open={isModalVisible}
          onCancel={handleCancel}
          footer={null}
          width={800}
          destroyOnClose={true}
        >
          <YouTubeSummarizer />
        </Modal>
      </>
    )
  }